import React from 'react';
import { useNavigate } from 'react-router-dom';

const EFFECTIVE_DATE = '16 April 2026';
const COMPANY = 'Avenor Software Ltd';
const SUPPORT_EMAIL = 'support@insightos.co.uk';
const DPO = 'Ashley Cook';

const H2 = ({ children }) => (
  <h2 className="text-xl font-bold mb-3 mt-2 text-blue-700">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-base font-semibold mb-2 text-blue-600">{children}</h3>
);
const UL = ({ children }) => (
  <ul className="list-disc ml-7 mb-4 space-y-1 text-gray-800">{children}</ul>
);
const P = ({ children }) => <p className="mb-4 text-gray-800">{children}</p>;

export default function TosStatic() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage:
          "linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(233,213,255,0.82) 45%, rgba(255,255,255,0.92) 100%), url('/avenorlogobackground.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="mb-8 inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 font-semibold transition-colors duration-150"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to Home
        </button>

        {/* Card */}
        <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-2xl p-10">
          {/* Header */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/avenor.png"
              alt="Avenor Software Ltd"
              className="h-12 mb-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="text-4xl font-extrabold text-blue-800 tracking-tight text-center">
              Terms of Service &amp; Privacy Policy
            </h1>
            <p className="text-gray-500 text-sm mt-2">
              {COMPANY} &mdash; Effective {EFFECTIVE_DATE}
            </p>
          </div>

          {/* ── Terms of Service ── */}
          <section className="mb-12">
            <h1 className="text-2xl font-bold mb-6 text-blue-700 border-b border-blue-100 pb-2">
              Terms of Service
            </h1>

            <section className="mb-8">
              <H2>0. Definitions</H2>
              <UL>
                <li><strong>"Platform"</strong> means the Avenor web-based software service operated by {COMPANY}, including Leads, Ad Studio, and Gallery.</li>
                <li><strong>"User"</strong> means any individual authorised to access the Platform under a registered account.</li>
                <li><strong>"Credits"</strong> means the token-based units of consumption purchased by the User to access billable features of the Platform.</li>
                <li><strong>"Content"</strong> means all data, text, leads, media, and materials submitted to or generated via the Platform.</li>
                <li><strong>"AI Services"</strong> means AI-powered features powered by third-party providers including OpenAI and Sora-2 Pro.</li>
                <li><strong>"Outreach"</strong> means email communications sent to third-party contacts via the Platform's SMTP integration.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>1. Use of the Platform</H2>
              <UL>
                <li><strong>Business Use Only:</strong> The Platform is licensed solely for legitimate business purposes by the registered account holder.</li>
                <li><strong>Single Named Users:</strong> Each login is personal to a single named individual. Sharing credentials, creating shared logins, or allowing unauthorised access is strictly prohibited.</li>
                <li>You are solely responsible for maintaining the security and confidentiality of your login credentials and for all activities that occur under your account.</li>
                <li>You may not use the Platform for any unlawful, fraudulent, defamatory, harmful, or abusive purpose.</li>
                <li>We reserve the right to suspend or permanently terminate any account that violates these Terms without notice or refund of unused credits.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>2. Credits, Purchasing &amp; Usage</H2>
              <UL>
                <li>Access to billable features of the Platform is governed by a credit-based system. Credits must be purchased in advance and are consumed when you use billable features.</li>
                <li>Credits are <strong>non-refundable</strong> once purchased, except where the Platform has been entirely unavailable due to a fault attributable to {COMPANY}.</li>
                <li>Credits do not expire and are shared across all features within your account (Leads, Ad Studio, Gallery).</li>
                <li>
                  The current credit costs are:
                  <ul className="list-disc ml-7 mt-1 space-y-0.5">
                    <li>1 credit per Companies House lead pull.</li>
                    <li>2 credits per lead enrichment with AI company intelligence.</li>
                    <li>10 credits per 4-second Sora-2 Pro ad video generation.</li>
                    <li>20 credits per 8-second Sora-2 Pro ad video generation.</li>
                    <li>30 credits per 12-second Sora-2 Pro ad video generation.</li>
                    <li>Bulk filtering, targeting, and AI sales reports are included at no additional credit cost.</li>
                  </ul>
                </li>
                <li>Credit pricing may be updated from time to time. Updated pricing will be communicated via the Platform before taking effect.</li>
                <li>All payments are processed securely by Stripe. By purchasing credits, you authorise the applicable one-off charge. No recurring billing is applied unless you explicitly enable it.</li>
                <li>You must maintain a valid payment method to purchase additional credits.</li>
                <li>Purchases of additional AI token usage beyond included allowances are charged as separate one-off payments and are non-refundable once consumed.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>3. Responsible Use &amp; Email Outreach Standards</H2>
              <P>
                The Avenor Platform includes SMTP-based email outreach functionality. By using outreach features, you accept full
                legal and ethical responsibility for all communications sent via the Platform. You agree to the following standards:
              </P>
              <H3>3.1 Professionalism &amp; Conduct</H3>
              <UL>
                <li>All outbound emails sent via the Platform must be professional in tone, accurate in content, and relevant to the recipient's business context.</li>
                <li>You must not send unsolicited bulk email ("spam"), misleading communications, or messages designed to deceive or manipulate recipients.</li>
                <li>You must clearly identify your business in all communications and provide a valid return email address.</li>
                <li>All outreach must include a clear, functional, and honoured opt-out mechanism in compliance with applicable law.</li>
              </UL>
              <H3>3.2 Legal Compliance</H3>
              <UL>
                <li>You are solely responsible for ensuring your email outreach complies with all applicable laws, including but not limited to:
                  <ul className="list-disc ml-7 mt-1 space-y-0.5">
                    <li>UK GDPR and the Data Protection Act 2018.</li>
                    <li>The Privacy and Electronic Communications Regulations 2003 (PECR).</li>
                    <li>The CAN-SPAM Act (where applicable to US recipients).</li>
                    <li>Any other applicable national or international data protection or electronic communications law.</li>
                  </ul>
                </li>
                <li>You must only contact individuals or businesses for whom you have a lawful basis to do so (e.g. legitimate interest in a B2B context, prior consent, or an existing business relationship).</li>
                <li>You must maintain accurate records of your lawful basis for each category of outreach you conduct.</li>
              </UL>
              <H3>3.3 Data Handling for Outreach</H3>
              <UL>
                <li>Lead data pulled from Companies House or enriched via the Platform must be used solely for the purpose for which it was collected — legitimate B2B outreach within your declared sector.</li>
                <li>You must not share, resell, or distribute lead data obtained via the Platform to third parties.</li>
                <li>You are responsible for honouring all opt-out requests promptly and updating your records accordingly.</li>
                <li>Misuse of the outreach feature, including sending harmful, harassing, or fraudulent messages, will result in immediate account suspension and may be reported to relevant authorities.</li>
              </UL>
              <H3>3.4 Ad Studio &amp; AI-Generated Content</H3>
              <UL>
                <li>AI-generated videos produced via Ad Studio must not contain defamatory, discriminatory, misleading, or illegal content.</li>
                <li>You are solely responsible for reviewing and approving all AI-generated content before use or publication.</li>
                <li>You must not use AI-generated content to impersonate individuals, organisations, or brands without explicit permission.</li>
                <li>Videos stored in your Gallery remain your responsibility. {COMPANY} accepts no liability for the content of AI-generated videos published or distributed by Users.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>4. AI-Generated Content &amp; Accuracy</H2>
              <UL>
                <li>The Platform uses AI services (including OpenAI and Sora-2 Pro) to generate leads, reports, call scripts, benefit documents, storyboards, and video content.</li>
                <li>AI outputs are provided on a best-efforts basis and may contain inaccuracies, hallucinations, or incomplete information. All AI-generated content must be reviewed and verified by a human before use.</li>
                <li>We hold no liability for decisions, actions, or losses arising from reliance on AI-generated content.</li>
                <li>AI API availability is subject to the terms and uptime of third-party providers. We do not guarantee continuous availability of AI features.</li>
                <li>By using AI features, you acknowledge and accept these limitations.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>5. Intellectual Property &amp; Restrictions</H2>
              <UL>
                <li>All source code, platform architecture, designs, features, branding, and proprietary methodologies are the exclusive intellectual property of {COMPANY}.</li>
                <li>You are granted a limited, non-exclusive, non-transferable licence to use the Platform solely for its intended business purpose under these Terms.</li>
                <li>You must not: copy, clone, reverse-engineer, decompile, disassemble, resell, sublicense, or create derivative works of any part of the Platform.</li>
                <li>Data scraping, screen scraping, or automated extraction of Platform data or features (other than through our published API, if any) is strictly prohibited.</li>
                <li>Any attempt to build a competing product using insights, features, or methodologies derived from the Platform constitutes a material breach of these Terms.</li>
                <li>Violations will result in immediate termination of access and may result in legal proceedings and claims for damages under UK intellectual property law.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>6. Data Ownership &amp; Usage</H2>
              <UL>
                <li>You retain full ownership of your business data uploaded to or generated by the Platform.</li>
                <li>By using the Platform, you grant {COMPANY} a limited, non-exclusive licence to process your data solely to provide the service.</li>
                <li>We do not access, resell, or distribute your data to third parties without explicit written permission, except as described in our Privacy Policy.</li>
                <li>You are solely responsible for the legality, accuracy, and appropriateness of all data you upload, generate, or distribute via the Platform.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>7. System Access, Availability &amp; Third-Party Vendors</H2>
              <UL>
                <li>We aim to maintain high platform availability but do not guarantee uninterrupted, error-free access.</li>
                <li>We are not liable for downtime or disruption caused by:
                  <ul className="list-disc ml-7 mt-1 space-y-0.5">
                    <li>Your internet connectivity or local infrastructure.</li>
                    <li>Third-party provider outages (e.g. IONOS Cloud VPS hosting, OpenAI, Sora-2 Pro, Stripe, FingerprintJS).</li>
                    <li>Scheduled or emergency maintenance (we will endeavour to provide advance notice where possible).</li>
                    <li>Force majeure events.</li>
                  </ul>
                </li>
                <li>Planned maintenance will be communicated via the Platform where reasonably practicable.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>8. Security, Privacy &amp; Encryption</H2>
              <UL>
                <li>All data is encrypted in transit (TLS/HTTPS) and at rest.</li>
                <li>MFA (Multi-Factor Authentication) is supported and recommended for all accounts.</li>
                <li>SMTP credentials are stored in encrypted form and never exposed in plaintext to any party.</li>
                <li>We maintain GDPR-compliant infrastructure hosted within the United Kingdom.</li>
                <li>We conduct regular security assessments and promptly remediate identified vulnerabilities.</li>
                <li>In the event of a data breach, we will notify affected users and relevant supervisory authorities as required by UK GDPR within 72 hours of becoming aware.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>9. Prohibited Activities &amp; Harmful Content</H2>
              <UL>
                <li>You agree not to use the Platform for any unlawful, abusive, harmful, or unethical purpose.</li>
                <li>
                  Prohibited activities include but are not limited to:
                  <ul className="list-disc ml-7 mt-1 space-y-0.5">
                    <li>Using the Platform for any illegal, fraudulent, or unauthorised purpose.</li>
                    <li>Submitting or generating content that is defamatory, harassing, threatening, hateful, racist, sexist, discriminatory, obscene, or violent.</li>
                    <li>Uploading or distributing copyrighted or proprietary material without lawful permission.</li>
                    <li>Attempting to exploit, reverse-engineer, clone, tamper with, or gain unauthorised access to the Platform or its underlying systems.</li>
                    <li>Using automated bots, scripts, or tools to interfere with or scrape the Platform.</li>
                    <li>Harvesting or collecting data from other users without explicit written consent.</li>
                    <li>Publishing content that could expose {COMPANY} to legal, financial, or reputational risk.</li>
                    <li>Sending spam, phishing messages, or unlawful commercial communications via the outreach feature.</li>
                  </ul>
                </li>
                <li>We reserve the right to investigate and take immediate action, including suspension or termination of access and referral to law enforcement, against any user engaging in prohibited conduct.</li>
                <li>You are solely responsible for all content submitted or actions taken through your account.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>10. GDPR Compliance &amp; Data Subject Rights</H2>
              <UL>
                <li>{COMPANY} is fully committed to compliance with UK GDPR and the Data Protection Act 2018.</li>
                <li>You may submit a Subject Access Request (SAR) at any time to access, correct, or delete personal data we hold about you.</li>
                <li>We will not share your personal data with third parties for marketing without your explicit consent.</li>
                <li>We will notify you of any data breach affecting your personal data within 72 hours as required by UK GDPR.</li>
                <li>Payment information is handled exclusively by Stripe. We do not store card numbers, sort codes, or bank account details.</li>
                <li>All data subject rights requests should be directed to our Data Protection Officer, <strong>{DPO}</strong>, at{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700 underline">{SUPPORT_EMAIL}</a>.
                </li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>11. Termination &amp; Suspension</H2>
              <UL>
                <li>We may suspend or terminate your account immediately and without refund of unused credits if:
                  <ul className="list-disc ml-7 mt-1 space-y-0.5">
                    <li>You violate any provision of these Terms.</li>
                    <li>Your actions pose a legal, regulatory, security, or reputational risk to {COMPANY} or the Platform.</li>
                    <li>You engage in prohibited activities or breach our data and outreach policies.</li>
                    <li>A payment dispute or fraudulent transaction is identified on your account.</li>
                  </ul>
                </li>
                <li>You may request account closure at any time by contacting{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700 underline">{SUPPORT_EMAIL}</a>.
                  Any unused credits are non-refundable upon voluntary closure.
                </li>
                <li>Upon termination, your right to access the Platform ceases immediately. We will retain your data for up to 30 days before permanent deletion, unless required by law to retain it longer.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>12. Limitation of Liability</H2>
              <UL>
                <li>The Platform is provided "as is" and "as available" without warranties of any kind, whether express or implied, including but not limited to merchantability, fitness for a particular purpose, or non-infringement.</li>
                <li>We do not guarantee that the Platform will be uninterrupted, error-free, or free from harmful components.</li>
                <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of revenue, loss of data, loss of business opportunities, or reputational harm, arising from your use of or inability to use the Platform.</li>
                <li>To the maximum extent permitted by law, our total aggregate liability to you shall not exceed the total value of credits purchased by you in the three (3) months prior to the event giving rise to the claim.</li>
                <li>Nothing in these Terms excludes liability for death or personal injury caused by our negligence, fraud, or fraudulent misrepresentation, or any other liability that cannot lawfully be excluded.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>13. Indemnification</H2>
              <UL>
                <li>You agree to indemnify, defend, and hold harmless {COMPANY}, its directors, officers, employees, agents, and licensors from and against any and all claims, damages, liabilities, costs, and expenses (including reasonable legal fees) arising from:
                  <ul className="list-disc ml-7 mt-1 space-y-0.5">
                    <li>Your use of or access to the Platform.</li>
                    <li>Your violation of any provision of these Terms.</li>
                    <li>Your violation of any third-party right, including intellectual property, privacy, or data protection rights.</li>
                    <li>Any outreach communications sent by you via the Platform that breach applicable law.</li>
                  </ul>
                </li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>14. Marketing &amp; Use of Your Logo</H2>
              <UL>
                <li>With your express written consent, we may reference your company name or display your logo in our marketing materials as a customer of the Platform.</li>
                <li>You may withdraw this consent at any time by contacting{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700 underline">{SUPPORT_EMAIL}</a>.
                </li>
                <li>We will not use your branding in any way that misrepresents your organisation or implies endorsement beyond your role as a Platform user.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>15. Intellectual Property — User Content</H2>
              <UL>
                <li>You retain ownership of all original content you upload or create on the Platform.</li>
                <li>AI-generated content produced using our Platform may have limitations under current UK copyright law. You are responsible for establishing your rights to use AI-generated content before publication or commercial use.</li>
                <li>We do not claim ownership of your lead data, enriched content, reports, or generated videos.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>16. Governing Law &amp; Jurisdiction</H2>
              <UL>
                <li>These Terms are governed by and construed in accordance with the laws of England and Wales.</li>
                <li>Any disputes arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.</li>
                <li>Nothing in this clause affects your statutory rights as a consumer (where applicable).</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>17. Cookies &amp; Tracking Technologies</H2>
              <UL>
                <li>We use strictly necessary cookies for authentication and session management.</li>
                <li>We use device fingerprinting (FingerprintJS) for fraud prevention and platform security. Full details are presented in our Cookie Consent banner.</li>
                <li>Where required by law, we request your consent before deploying non-essential cookies or fingerprinting technologies.</li>
                <li>You may manage or withdraw cookie consent via your browser settings. Disabling essential cookies may impair your ability to use the Platform.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>18. Force Majeure</H2>
              <P>
                {COMPANY} shall not be liable for any failure or delay in performing its obligations where such failure or delay
                results from events beyond our reasonable control, including but not limited to acts of God, war, terrorism, pandemic,
                civil unrest, government action, internet infrastructure failures, or third-party service provider outages.
              </P>
            </section>

            <section className="mb-8">
              <H2>19. Changes to These Terms</H2>
              <UL>
                <li>We may update these Terms from time to time to reflect changes in law, our services, or our business practices.</li>
                <li>Material changes will be communicated via the Platform or by email with reasonable notice.</li>
                <li>Continued use of the Platform following notification of updated Terms constitutes your acceptance of those changes.</li>
                <li>If you do not accept updated Terms, you must cease using the Platform and contact us to close your account.</li>
              </UL>
            </section>

            <section className="mb-8">
              <H2>20. Contact &amp; Complaints</H2>
              <P>
                For any questions, concerns, or complaints regarding these Terms or the Platform, please contact:<br />
                <strong>Company:</strong> {COMPANY}<br />
                <strong>Data Protection Officer:</strong> {DPO}<br />
                <strong>Email:</strong>{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700 underline">{SUPPORT_EMAIL}</a>
              </P>
              <P>
                If you are not satisfied with our response, you have the right to lodge a complaint with the
                Information Commissioner's Office (ICO) at{' '}
                <a href="https://ico.org.uk" className="text-blue-700 underline" target="_blank" rel="noreferrer">ico.org.uk</a>.
              </P>
            </section>
          </section>

          {/* ── Privacy Policy ── */}
          <section className="mb-8">
            <h1 className="text-2xl font-bold mb-6 text-blue-700 border-b border-blue-100 pb-2">
              Privacy Policy
            </h1>

            <P><strong>Effective Date:</strong> {EFFECTIVE_DATE}</P>
            <P><strong>Company:</strong> {COMPANY}</P>
            <P><strong>Jurisdiction:</strong> United Kingdom</P>

            <H2>1. Introduction</H2>
            <P>
              {COMPANY} is committed to protecting your personal data and respecting your privacy. This Privacy Policy explains how we
              collect, use, store, and safeguard information when you use the Avenor platform and its associated services, including
              Leads, Ad Studio, and Gallery.
            </P>

            <H2>2. Information We Collect</H2>
            <UL>
              <li><strong>Account Data:</strong> Name, email address, company name, contact details, and role provided during registration or account management.</li>
              <li><strong>Usage Data:</strong> Login times, pages accessed, features used, lead actions, credit consumption, and generated content metadata.</li>
              <li><strong>SMTP Credentials:</strong> If you configure your own email provider for outreach, those credentials are stored encrypted and used solely to send emails on your behalf.</li>
              <li><strong>Generated Content:</strong> Lead pull results, enriched data, AI-generated call scripts, benefit reports, and ad studio videos stored in your Gallery.</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device characteristics, and device fingerprint (for fraud and security purposes — see Cookie Policy).</li>
              <li><strong>Billing Data:</strong> Payment is processed exclusively through Stripe. We do not store card numbers or sensitive payment credentials.</li>
            </UL>

            <H2>3. How We Use Your Information</H2>
            <UL>
              <li>To provide, maintain, and improve the Avenor platform and its features.</li>
              <li>To process credit purchases and manage your account balance.</li>
              <li>To send platform notifications, account alerts, and support communications.</li>
              <li>To generate AI-powered leads, reports, call scripts, and ad content on your behalf.</li>
              <li>To detect, prevent, and investigate fraud, abuse, and security threats.</li>
              <li>To comply with legal and regulatory obligations under UK law.</li>
              <li>To personalise your experience and improve platform performance.</li>
            </UL>

            <H2>4. Legal Basis for Processing (UK GDPR)</H2>
            <UL>
              <li><strong>Contractual necessity:</strong> Processing required to deliver our services to you.</li>
              <li><strong>Consent:</strong> When you accept these Terms and proceed with registration.</li>
              <li><strong>Legitimate interests:</strong> Fraud prevention, security monitoring, and platform improvement.</li>
              <li><strong>Legal obligation:</strong> Where processing is required by applicable law.</li>
            </UL>

            <H2>5. Data Sharing and Disclosure</H2>
            <UL>
              <li>We do <strong>not</strong> sell, rent, or trade your personal data.</li>
              <li>We share data only with trusted third-party processors necessary to deliver our service (e.g. Stripe for payments, OpenAI for AI generation, IONOS for hosting, FingerprintJS for fraud prevention, Bunny.net for media delivery and CDN) under strict data processing agreements.</li>
              <li><strong>Bunny.net:</strong> We use Bunny.net as a data processor for media content, including images and videos uploaded to or generated by the Platform. Bunny.net acts as our Content Delivery Network (CDN) and media storage provider. Data transferred to Bunny.net is subject to their data processing agreement and privacy policy. Media files may be stored and served from Bunny.net infrastructure located within or outside the United Kingdom; where transfers occur outside the UK, appropriate safeguards are in place.</li>
              {process.env.REACT_APP_IS_AVENOR !== 'true' && (
                <li><strong>InsightOS Ltd (Sister Company):</strong> Your data may be shared with InsightOS Ltd, our sister company, for purposes including but not limited to platform security monitoring, fraud prevention, identity verification, marketing, product improvement, and the delivery of integrated or complementary services. InsightOS Ltd processes data under equivalent data protection standards and is bound by the same UK GDPR obligations. By accepting these Terms, you acknowledge and consent to this data sharing arrangement. You may object to this processing by contacting our Data Protection Officer at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700 underline">{SUPPORT_EMAIL}</a>.</li>
              )}
              <li>We may disclose data to law enforcement or regulatory bodies where required by law or to protect our legal rights.</li>
              <li>In the event of a business transfer (merger, acquisition), data may be transferred to the successor entity under equivalent protections.</li>
            </UL>

            <H2>6. Data Security</H2>
            <UL>
              <li>All data is encrypted in transit (TLS) and at rest.</li>
              <li>SMTP credentials are stored in encrypted form and never exposed in plaintext.</li>
              <li>Access to personal data is strictly limited to authorised personnel.</li>
              <li>Multi-Factor Authentication (MFA) is available and encouraged for all accounts.</li>
              <li>We conduct regular security assessments and promptly address vulnerabilities.</li>
              <li>We will notify affected users of any data breach within 72 hours of becoming aware, as required by UK GDPR.</li>
            </UL>

            <H2>7. Data Retention</H2>
            <P>
              We retain your data for as long as your account remains active and as necessary to provide our services. Upon account
              deletion or termination, your data will be securely deleted within 30 days unless retention is required by law or for
              legitimate dispute resolution purposes.
            </P>

            <H2>8. Your Rights Under UK GDPR</H2>
            <P>You have the right to:</P>
            <UL>
              <li><strong>Access:</strong> Request a copy of your personal data (Subject Access Request).</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Erasure:</strong> Request deletion of your data where there is no lawful basis for continued processing.</li>
              <li><strong>Restriction:</strong> Request that we limit how we use your data in certain circumstances.</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format.</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests.</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time without affecting prior processing.</li>
            </UL>
            <P>
              To exercise any of these rights, contact our Data Protection Officer: <strong>{DPO}</strong> at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700 underline">{SUPPORT_EMAIL}</a>.
            </P>

            <H2>9. Cookies and Device Fingerprinting</H2>
            <P>
              We use session cookies for authentication and security. We also use device fingerprinting technology (FingerprintJS) for
              fraud prevention and platform integrity. For full details, please see our Cookie Policy presented at first visit.
              You may manage cookie preferences through your browser settings, however disabling essential cookies may affect your
              ability to use the platform.
            </P>

            <H2>10. International Data Transfers</H2>
            <P>
              Your data is stored and processed primarily within the United Kingdom. Where data is transferred outside the UK,
              we ensure appropriate safeguards are in place (e.g. UK adequacy decisions, Standard Contractual Clauses).
            </P>

            <H2>11. Changes to This Privacy Policy</H2>
            <P>
              We may update this policy periodically. Any changes will be posted with an updated effective date. Continued use of the
              platform following notification constitutes acceptance of the revised policy.
            </P>

            <H2>12. Contact &amp; Data Protection Officer</H2>
            <P>
              <strong>Data Protection Officer:</strong> {DPO}<br />
              <strong>Company:</strong> {COMPANY}<br />
              <strong>Email:</strong>{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700 underline">{SUPPORT_EMAIL}</a>
            </P>
          </section>

          {/* Back button (bottom) */}
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-8 rounded-lg transition-all duration-150 shadow-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
